import { parseCookies, setCookie, destroyCookie } from 'nookies';
import { GetServerSidePropsContext } from 'next';
import { BehaviorSubject, from } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { IncomingMessage } from 'http';

import { AjaxRequestOptions, UntypedObject } from '@/types';
import { getApiUrl, API_URL } from '@/utils/api';
import { isSSR } from '@/utils/millesimaUtils';
import { ActAsCustomer } from '@/models';

import { LoggerService } from './Logger.service';
import { COOKIES } from '@/constants/cookies';
import { LOG_MSG } from '@/constants/logger';

export class AjaxError extends Error {
  constructor(public error: any, public status: any) {
    super('AjaxError');
  }
}

// Isomorphic Observable ajax request.
export class AjaxService {
  static AUTHORIZATION_HEADER = 'Authorization';
  static ACT_AS_CUSTOMER_ID_HEADER = 'X-ACT-AS-CUSTOMER-ID';
  static GUEST_TOKEN_HEADER = 'x-guest-token';
  static IS_MOBILE_HEADER = 'X-Is-Mobile';
  static HTTPS = 'https';

  static userApiUrl = getApiUrl(API_URL.USER);

  // Fake log function when no debugId is supplied to the constructor
  log = (..._args: any) => undefined;

  // Tokens
  private _actAsCustomerEvent = new BehaviorSubject<ActAsCustomer>(undefined);
  private _actAsCustomer?: ActAsCustomer;

  private _authTokenEvent = new BehaviorSubject<string>(undefined);
  private _authToken?: string;

  // The current request context
  private _ctx?: GetServerSidePropsContext;
  private _isMobileDevice: boolean;
  private _isSafari: boolean;
  private _isGoogleBot: boolean;
  private _absoluteUrl: string;
  private _https: boolean;

  constructor(public readonly debugId?: number) {
    if (this.debugId) {
      this.log = this.debugLog;
    }

    this.log('CONSTRUCTOR');

    if (!isSSR()) {
      this.saveRequestContext(undefined);
    }
  }

  // GETTERS

  /**
   * The application base URL
   */
  get baseUrl() {
    return this._absoluteUrl;
  }

  /**
   * The current request context in SSR mode
   */
  get pageContext() {
    return this._ctx?.req;
  }

  /**
   * True if on a HTTPS connection link
   */
  get isHttps() {
    return this._https;
  }

  /**
   * The auth token observable
   */
  get authToken$() {
    return this._authTokenEvent.asObservable();
  }

  /**
   * The current authToken value
   */
  get authToken() {
    return this._authToken;
  }

  /**
   * The ActAs customer observable
   */
  get actAsCustomer$() {
    return this._actAsCustomerEvent.asObservable();
  }

  /**
   * The current ActAs customer value
   */
  get actAsCustomer() {
    return this._actAsCustomer;
  }

  // BACKEND ACCESS

  getJSON(url: string, options?: AjaxRequestOptions) {
    return this.requestJSON(url, options);
  }

  getPromisedJSON(url: string, options?: AjaxRequestOptions) {
    return this.requestPromisedJSON(url, options);
  }

  getTextCSV(url: string, options?: AjaxRequestOptions) {
    return this.requestTextCSV(url, options);
  }

  postJSON(url: string, payload: any, options?: AjaxRequestOptions) {
    return this.requestWithBody(url, payload, options, 'POST');
  }

  postPromisedJSON(url: string, payload: any, options?: AjaxRequestOptions) {
    return this.requestWithBodyAndPromise(url, payload, options, 'POST');
  }

  putJSON(url: string, payload: any, options?: AjaxRequestOptions) {
    return this.requestWithBody(url, payload, options, 'PUT');
  }

  putPromisedJSON(url: string, payload: any, options?: AjaxRequestOptions) {
    return this.requestWithBodyAndPromise(url, payload, options, 'PUT');
  }

  deleteJSON(url: string, payload?: any, options?: AjaxRequestOptions) {
    return this.requestWithBody(url, payload, options, 'DELETE');
  }

  // REQUEST CONTEXT HELPER

  saveRequestContext(ctx?: GetServerSidePropsContext) {
    this._ctx = ctx;
    this.initializeService(ctx?.req);
    return this.getContext();
  }

  getContext() {
    return {
      actAsCustomer: this.actAsCustomer,
      authToken: this.authToken,
      isMobile: this._isMobileDevice,
      isSafari: this._isSafari,
      baseUrl: this.baseUrl,
      isHttps: this.isHttps,
      isGoogleBot: this._isGoogleBot
    };
  }

  /**
   * Initialize service internal settings
   * @param {object} req The current request (SSR) or undefined (BROWSER)
   */
  private initializeService(req?: IncomingMessage) {
    this.log('INITIALIZE SERVICE enter', !!req);

    let navigatorUserAgent = '';
    if (typeof navigator !== 'undefined') {
      navigatorUserAgent = navigator.userAgent;
    }

    // Retrieve the request user agent to initialize the mobile flag
    const userAgent = req?.headers?.['user-agent'] || navigatorUserAgent;
    const isMobile = Boolean(userAgent && userAgent.match(
      /Android|BlackBerry|iPhone|iPod|Opera Mini|IEMobile|WPDesktop/i
    ));
    const isSafari = Boolean(userAgent &&
      userAgent.includes('Safari') &&
      !userAgent.includes('Chrome') &&
      !userAgent.includes('CriOS')
    );
    const isGoogleBot = Boolean(userAgent && userAgent.match(
      /GoogleBot/i
    ));

    const [protocol] = (req ? req.headers?.['referer'] || AjaxService.HTTPS.concat(':') : window.location.href).split(':');
    const host = req ? req.headers?.['host'] : window.location.hostname;

    this._https = protocol === AjaxService.HTTPS;
    this._absoluteUrl = `${protocol}://${host}`;
    this._isMobileDevice = isMobile;
    this._isSafari = isSafari;
    this._isGoogleBot = isGoogleBot;

    this.getTokenFromCookies();
    this.getActAsCustomerFromCookies();

    this.log(
      'INITIALIZE SERVICE exit',
      this._https, this._absoluteUrl, this._isMobileDevice, this._isSafari,
      this.authToken?.slice(0, 10), this.actAsCustomer?._id
    );
  }

  // HELPERS

  private debugLog(...args: any[]): void {
    console.log(`MEP: AJAX[${this.debugId}]`, ...args);
  }

  requestWithBody(url: string, payload: any, options, method = 'POST') {
    const combinedOptions = {
      ...options,
      method,
      body: typeof payload === 'object' ? JSON.stringify(payload) : payload
    };
    return this.requestJSON(url, combinedOptions);
  }

  requestWithBodyAndPromise(url: string, payload: any, options, method = 'POST') {
    const combinedOptions = {
      ...options,
      method,
      body: typeof payload === 'object' ? JSON.stringify(payload) : payload
    };
    return this.requestPromisedJSON(url, combinedOptions);
  }

  requestJSON(url: string, options: AjaxRequestOptions = {}) {
    if (options.noJsonHeader == null || !options.noJsonHeader) {
      options.headers = options.headers || {};
      options.headers['Accept'] = 'application/json';
      options.headers['Content-Type'] = 'application/json';
    }

    if (options.cookies) {
      options.headers['Cookie'] = options.cookies;
    }

    return from(this.request(url, options));
  }

  requestPromisedJSON(url: string, options: AjaxRequestOptions = {}) {
    if (options.noJsonHeader == null || !options.noJsonHeader) {
      options.headers = options.headers || {};
      options.headers['Accept'] = 'application/json';
      options.headers['Content-Type'] = 'application/json';
    }

    if (options.cookies) {
      options.headers['Cookie'] = options.cookies;
    }

    return this.request(url, options);
  }

  requestTextCSV(url: string, options: AjaxRequestOptions = {}) {
    options.headers = options.headers || {};
    options.headers['Accept'] = 'text/csv';
    options.headers['Content-Type'] = 'text/csv';
    return from(this.request(url, options));
  }

  private buildHeaders(headers: AjaxRequestOptions['headers'] = {}) {
    headers[AjaxService.IS_MOBILE_HEADER] = !!this._isMobileDevice;

    if (this.authToken) {
      headers[AjaxService.AUTHORIZATION_HEADER] = `Bearer ${this.authToken}`;
    }

    if (this.actAsCustomer) {
      headers[AjaxService.ACT_AS_CUSTOMER_ID_HEADER] = this.actAsCustomer._id;
    }

    return headers;
  }

  async request(url: string, options: AjaxRequestOptions = {}) {
    const guestRoute = !this.authToken && options.guestToken;

    options.headers = this.buildHeaders(options.headers);

    this.log('HEADER [AUTH,ACTAS]', url, this.authToken?.slice(0, 10), this.actAsCustomer?._id);

    if (guestRoute) {
      // Special route accessible when not authenticated (no auth token present)
      // and that will return a guest token in the response header.
      // Add a specific suffix to the normal route.
      // ATTENTION: the route is assumed to have no query parameters !
      this.log('ADD GUEST SUFFIX ROUTE', url);
      url += 'Guest';
    }

    if (options.textCSV) {
      return await this.handleTextCSVRequest(url, options);
    } else {
      return await this.handleJSONRequest(url, options, guestRoute);
    }
  }

  async handleTextCSVRequest(url: string, options: AjaxRequestOptions) {
    try {
      const res = await fetch(url, options);
      const buffer = await res.arrayBuffer();
      const decoder = new TextDecoder('iso-8859-1');
      const text = decoder.decode(buffer);

      return text;
    } catch (err) {
      throw this.handleRequestError(options, url, err);
    }
  }

  async handleJSONRequest(url: string, options: AjaxRequestOptions, guestRoute?: boolean) {
    try {
      const res = await fetch(url, options);

      if (guestRoute) {
        const guestToken = res.headers.get(AjaxService.GUEST_TOKEN_HEADER);
        this.log('GET GUEST TOKEN FROM RESPONSE', guestToken);
        this.setAuthTokenCookie(guestToken);
      }

      const response = await this.parseJSON(options, res);

      if (response.ok) {
        return response.json;
      }

      this.log('RESPONSE ERROR [STATUS] !!!', response.status);

      if (response.status === 401) {
        this.setAuthTokenCookie(undefined);
      }

      throw this.ajaxException(options, response.json, response.status);
    } catch (err) {
      if (err instanceof AjaxError) {
        throw err;
      }
      throw this.handleRequestError(options, url, err);
    }
  }

  handleRequestError(options: AjaxRequestOptions, url: string, err: any) {
    // Build a generic error before throwing it

    return this.ajaxException(options, {
      errorCode: '0',
      status: `${JSON.stringify(err)} -- url : ${url} -- options : ${JSON.stringify(options)}`,
    }, err.status);
  }

  ajaxException({ silentError }: AjaxRequestOptions, error, status) {
    if (!silentError) {
      LoggerService.sendAjaxErrorLog(error, status);
    }
    return new AjaxError(error, status);
  }

  parseJSON({ noJsonHeader, silentError }: AjaxRequestOptions, response) {
    return response.text().then((text) => {
      if (!noJsonHeader && text.length > 0) {
        return {
          json: JSON.parse(text) || {},
          status: response.status,
          ok: response.ok
        };
      }
      return {
        json: text || '',
        status: response.status,
        ok: response.ok
      };
    }).catch((error) => {
      if (!silentError) {
        LoggerService.sendAjaxErrorLog('Ajax response JSON parse error', error.message);
      }
      return {
        json: {},
        status: 500,
        ok: false
      };
    });
  }

  // AUTH TOKEN & COOKIE

  /**
   * Get the connected user from the cookie (works on both SSR and browser sides)
   * Push it to the observable
   */
  getTokenFromCookies() {
    try {
      this._authToken = parseCookies(this._ctx)[COOKIES.AUTH.NAME];
      this.log('AUTH COOKIE', this._authToken?.slice(0, 10));
      this._authTokenEvent.next(this._authToken);

      if (!this._authToken) {
        this.setActAsCustomerCookie(undefined);
      }
    } catch (err) {
      console.error('Error parsing cookie AJAX SERVICE : 409');
      LoggerService.sendLog(null, LOG_MSG.AUTH.DISCONNECT, null, null, null, {
        cookies: this._ctx?.req?.cookies,
        ctx: this._ctx,
        error: err
      });
      this.setAuthTokenCookie(undefined);
    }
  }

  /**
   * Decode token received in the response body of an authentication request.
   * @param {string} accessToken The authentication response body accessToken attribute.
   */
  storeTokenFromBody(accessToken: string): void {
    this.setAuthTokenCookie(accessToken, false);
  }

  /**
   * Store an auth token to the auth cookie (the JWT token is saved in a cookie).
   * Or delete the auth cookie if the given token is undefined.
   * @param {string} token The auth token
   * @param {boolean} dispatch True event must be dispatched
   */
  setAuthTokenCookie(token: string, dispatch = true): void {
    const options = {
      sameSite: this.isHttps ? 'none' : 'lax',
      maxAge: COOKIES.AUTH.LIFETIME,
      secure: this.isHttps,
      httpOnly: false,
      path: '/'
    };

    if (token) {
      setCookie(this._ctx, COOKIES.AUTH.NAME, token, options);
    } else {
      // Set the cookie to empty because destroyCookie can fail on firefox 70.0.1
      setCookie(this._ctx, COOKIES.AUTH.NAME, '', options);
      destroyCookie(this._ctx, COOKIES.AUTH.NAME, options);
      // When not connected anymore, also destroy the actas cookie
      this.setActAsCustomerCookie(undefined);
    }

    this._authToken = token;

    if (dispatch) {
      this._authTokenEvent.next(token);
    }
  }

  // ACTASCUSTOMER COOKIE

  /**
   * Get the act as custommer from the cookie (works on both SSR and browser sides)
   * Push it to the observable
   */
  getActAsCustomerFromCookies(): void {
    try {
      const data = parseCookies(this._ctx)[COOKIES.ACT_AS.NAME];
      this.log('ACTAS CUSTOMER COOKIE', data);
      if (data) {
        this._actAsCustomer = this.deserializeActAsCustomer(data);
        this.log('ACTAS CUSTOMER DECODED', this._actAsCustomer._id);
        this._actAsCustomerEvent.next(this._actAsCustomer);
      }
    } catch (_error) {
      // Unprocessed error
      console.log('ACT AS COOKIE ERR', _error);
    }
  }

  /**
   * Store ActAs customer into a cookie
   * @param {*} customer The customer to save
   */
  setActAsCustomerCookie(customer: UntypedObject): void {
    const options = {
      path: '/',
      maxAge: COOKIES.ACT_AS.LIFETIME
    };

    this.log('setActAsCustomerCookie', customer);
    this._actAsCustomer = this.serializeActAsCustomer(customer);

    if (this._actAsCustomer) {
      setCookie(this._ctx, COOKIES.ACT_AS.NAME, JSON.stringify(this._actAsCustomer), options);
    } else {
      destroyCookie(this._ctx, COOKIES.ACT_AS.NAME, options);
    }

    this._actAsCustomerEvent.next(this._actAsCustomer);
  }

  serializeActAsCustomer(customer?: UntypedObject): ActAsCustomer | undefined {
    this.log('serializeActAsCustomer', customer?._id);
    if (typeof customer === 'object') {
      const { _id, firstName, lastName, pickId } = customer;
      return { _id, firstName, lastName, pickId };
    }
    return undefined;
  }

  deserializeActAsCustomer(data: string): ActAsCustomer | undefined {
    try {
      const { _id, firstName, lastName, pickId } = JSON.parse(data);
      return { _id, firstName, lastName, pickId };
    } catch (err) {
      console.error('AjaxService: wrong ActAs cookie', err);
      return undefined;
    }
  }
}
